import { inject } from "@angular/core";
import { init as initFMS } from "@storebrand-digital/fms-translations";
import { FmsService, FmsVersion } from "src/app/services/fms.service";
import { CookieService } from "src/app/services/cookie.service";

/**
 * FMS initializer that ensures FMS translations are loaded before the app starts.
 * This prevents race conditions where components try to use FMS before it's ready.
 */
export const fmsInitializer = async (): Promise<void> => {
  const fmsService = inject(FmsService);
  const cookieService = inject(CookieService);

  // Check for language in URL query parameters (e.g., ?lang=en)
  const getLangFromUrl = (): Fms.LangCode | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("lang") as Fms.LangCode;
  };

  // Set up language from cookie
  const getLanguageFromCookie = (): Fms.LangCode => {
    const language = cookieService.getCookie("lang");
    if (!language) {
      return "no";
    }
    return language === "en" ? "en" : "no";
  };

  // Determine language priority: URL param > Cookie > Default
  const urlLang = getLangFromUrl();
  const cookieLanguage = getLanguageFromCookie();

  const hasValidQueryParamLanguage = urlLang && fmsService.isValidLocale(urlLang);
  if (hasValidQueryParamLanguage) {
    fmsService.setLocale(urlLang);
  } else if (cookieLanguage) {
    fmsService.setLocale(cookieLanguage);
  }

  // Initialize FMS
  const version = await fmsService.getVersion();
  const clientId = "smart-pensjon-web";

  await initFMS({
    clientId,
    documentIds: ["nyt"],
    version,
    customerApiUrl: version === FmsVersion.Pending ? "https://api-t.storebrand.no" : "https://api.storebrand.no",
    getFallbackTextsForLanguage: async (language) => {
      if (language === "en") {
        const { default: en } = await import("../../assets/fms/fallback-en.json");
        return en;
      }
      const { default: no } = await import("../../assets/fms/fallback-no.json");
      return no;
    },
  });
};
